/*
 * Renders the add modal's access-check result beneath the folder input: the confirmed folder, or the
 * warning that explains why it cannot be added and, for the one fixable case, the account to share it
 * with. Kept apart from AddBoxFolderModal so the modal stays focused on input, settings, and enrollment.
 */
import React from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { type ProbeState } from 'src/components/box-folders/BoxFolderProbeIndicator';
import { BOX_SYSTEM_ACCOUNT_IDENTITY } from 'src/constants';
import { type BoxFolder } from 'src/types/sync-target.types';
import { Logger } from 'src/utils/logger';

/**
 * Reserved height for the probe-result region once the user has typed, sized to the tallest
 * state: the warning alert holding two lines of copy plus its address line. Holding the space
 * means a result appearing or changing never resizes the modal mid-interaction. Re-measure
 * whenever that alert gains or loses a row.
 */
const STATUS_MIN_HEIGHT = 72;

export const isProbeAlertState = (probeState: ProbeState): boolean =>
  probeState === 'inaccessible' || probeState === 'unavailable';

interface BoxFolderProbeStatusProps {
  folder: BoxFolder | undefined;
  probeState: ProbeState;
}

const SystemAccountIdentity: React.FC = () => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();

  /**
   * The address is far too long and too arbitrary to retype from a screen, so copying it
   * has to work. `navigator.clipboard` is absent outside a secure context and can reject
   * on a permissions policy, and both cases must report rather than silently no-op —
   * `await undefined` resolves, which would otherwise show a success toast for nothing.
   */
  const handleCopyIdentity = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(BOX_SYSTEM_ACCOUNT_IDENTITY);
      createToast({ type: AlertType.SUCCESS, message: t('box_folder_identity_copied') });
    } catch (caught) {
      Logger.error('Failed to copy the Box system account address', {
        detail: caught instanceof Error ? caught.message : String(caught),
      });
      // The address stays on screen and selectable, so the fallback is a real one.
      createToast({ type: AlertType.ERROR, message: t('box_folder_copy_failed') });
    }
  };

  return (
    // On its own line rather than inside the sentence: the address is long
    // enough to wrap mid-instruction, and it needs the copy button adjacent.
    <Row alignmentVertical="center" spacing="100">
      <Text data-testid="add-box-folder-identity" type="b200">
        {BOX_SYSTEM_ACCOUNT_IDENTITY}
      </Text>
      <Button
        aria-label={t('box_folder_copy_identity')}
        data-testid="add-box-folder-copy-identity-button"
        size="small"
        type="icon"
        onClick={() => { void handleCopyIdentity(); }}
      >
        <Icon tokens={copyTokens} />
      </Button>
    </Row>
  );
};

export const BoxFolderProbeStatus: React.FC<BoxFolderProbeStatusProps> = ({ folder, probeState }) => {
  const { t } = useTranslation();

  return (
    <Column data-testid="add-box-folder-status" minHeight={STATUS_MIN_HEIGHT}>
      {probeState === 'ok' && folder && (
        <Text data-testid="add-box-folder-resolved" type="b300">
          {t('box_folder_verified', {
            name: folder.name,
            count: String(folder.itemCollection?.totalCount ?? 0),
          })}
        </Text>
      )}

      {isProbeAlertState(probeState) && (
        // An Alert rather than only the tooltip: a folder we cannot see is the one
        // failure the user can actually fix, so it needs to be impossible to miss.
        <Alert data-testid="add-box-folder-alert" size="small" type="warning">
          <Column spacing="200">
            <Text>
              {probeState === 'inaccessible'
                ? t('box_folder_inaccessible')
                : t('box_folder_unavailable')}
            </Text>
            {probeState === 'inaccessible' && <SystemAccountIdentity />}
          </Column>
        </Alert>
      )}
    </Column>
  );
};
